/**
 * A file of the app could not be loaded (a server restarting, a new deploy with
 * other file names, a network blip). A browser keeps such a failure for the life of
 * the page, so only a fresh page gets past it. Without this the page stays empty,
 * or the click that needed the file just does nothing (I-3641).
 */
import type { Router } from "vue-router";
import {
  connectTileHasUnsavedWork,
  connectTileId,
  reportConnectTile,
} from "@blpde/connect";
import { useNxToasts } from "@blpde/nx-ui";
import i18n from "@/i18n";

const LOAD_FAILURE =
  /dynamically imported module|importing a module script failed|failed to load module script|unable to preload css|error loading/i;

/** Fresh pages per page within the window below, so a broken server is no endless loop. */
const MAX_RELOADS = 2;
const RELOAD_WINDOW_MS = 60_000;

export function isLoadFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return LOAD_FAILURE.test(message);
}

/** Whether another fresh page is still in the budget; counts it. */
export function mayReloadForLoadFailure(
  key: string,
  now = Date.now(),
): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    const last = raw
      ? (JSON.parse(raw) as { count?: number; at?: number })
      : {};
    const fresh = now - (last.at ?? 0) > RELOAD_WINDOW_MS;
    const count = fresh ? 0 : (last.count ?? 0);
    if (count >= MAX_RELOADS) return false;
    sessionStorage.setItem(key, JSON.stringify({ count: count + 1, at: now }));
    return true;
  } catch {
    return false;
  }
}

function freshPage(target?: string): void {
  if (target) window.location.hash = target;
  window.location.reload();
}

function tellUser(): void {
  useNxToasts().showToast(i18n.global.t("connect.tiles.loadFailed"), "error");
}

/**
 * - a tile on its way to its first page: the page around it gives it a fresh frame;
 * - any other page on its way to its first page: a fresh page, it has nothing to lose;
 * - a page in use: a fresh page at the target if nothing unsaved is at stake.
 *   Outside a tile that is not known, so the user decides.
 */
export function installLoadFailureRecovery(router: Router): void {
  const tileId = connectTileId();
  const budgetKey = `connect.loadRetry.${tileId ?? "page"}`;
  let reachedFirstPage = false;
  router.afterEach((_to, _from, failure) => {
    if (!failure) reachedFirstPage = true;
  });

  function recover(error: unknown, target?: string): void {
    const loadFailed = isLoadFailure(error);
    if (!reachedFirstPage) {
      if (tileId) reportConnectTile({ failed: true });
      else if (loadFailed && mayReloadForLoadFailure(budgetKey))
        freshPage(target);
      else tellUser();
      return;
    }
    if (!loadFailed) return;
    if (tileId && !connectTileHasUnsavedWork()) {
      if (mayReloadForLoadFailure(budgetKey)) freshPage(target);
      else reportConnectTile({ failed: true });
      return;
    }
    tellUser();
  }

  router.onError((error, to) => recover(error, to?.fullPath));
  // A build's preloaded file (mostly its CSS): the same, without a router error.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    recover(
      (event as Event & { payload?: unknown }).payload ?? "error loading",
    );
  });
}
